/**
 * @file formatAddress.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-16
 * @description Shared address formatting utility for display.
 *              Addresses are stored with country (as Xero sends them) but
 *              country/country codes are never displayed in the UI.
 */

// Country values to strip from display (Xero sends these)
const COUNTRY_VALUES = new Set([
  'gbr', 'gb', 'uk', 'united kingdom', 'great britain',
  'irl', 'ie', 'ireland', 'republic of ireland',
  'usa', 'us', 'united states', 'united states of america',
  'aus', 'au', 'australia',
  'nzl', 'nz', 'new zealand',
  'can', 'ca', 'canada',
  'fra', 'fr', 'france',
  'deu', 'de', 'germany',
  'esp', 'es', 'spain',
  'ita', 'it', 'italy',
  'nld', 'nl', 'netherlands',
]);

/**
 * Check if a string looks like a country name/code
 */
function isCountryValue(str) {
  if (!str) return false;
  return COUNTRY_VALUES.has(str.trim().toLowerCase());
}

/**
 * Strip trailing country from a plain string address.
 * Handles both comma-separated and newline-separated.
 */
function stripCountryFromString(str, separator) {
  if (!str) return str;
  const sep = separator === '\n' ? '\n' : ', ';
  const parts = str.split(sep);
  if (parts.length > 1 && isCountryValue(parts[parts.length - 1])) {
    parts.pop();
  }
  return parts.join(sep);
}

/**
 * Extract address parts from an object, handling all field name conventions.
 * Supports camelCase (Xero), snake_case (DB), and short names (flat columns).
 */
function extractParts(addr, { includeAttention = false } = {}) {
  if (!addr) return [];
  const parts = [];
  if (includeAttention) {
    parts.push(addr.attentionTo || addr.attention);
  }
  parts.push(addr.addressLine1 || addr.address_line_1 || addr.line1);
  parts.push(addr.addressLine2 || addr.address_line_2 || addr.line2);
  parts.push(addr.addressLine3 || addr.address_line_3 || addr.line3);
  parts.push(addr.addressLine4 || addr.address_line_4 || addr.line4);
  parts.push(addr.city);
  parts.push(addr.region);
  parts.push(addr.postalCode || addr.postal_code || addr.postcode);
  // Country intentionally excluded from display
  return parts.filter(p => p && String(p).trim());
}

/**
 * Format an address for display. Handles objects, JSON strings, and plain strings.
 * Country is always excluded from the output.
 *
 * @param {Object|string} address - Address object, JSON string, or plain string
 * @param {Object} [options]
 * @param {string} [options.separator=', '] - Join separator (', ' or '\n')
 * @param {boolean} [options.includeAttention=false] - Include attentionTo field
 * @param {string} [options.stripName=null] - Remove this name from first line (Xero PO quirk)
 * @param {string} [options.fallback=''] - Return value when address is empty
 * @returns {string}
 */
export function formatAddress(address, options = {}) {
  const { separator = ', ', includeAttention = false, stripName = null, fallback = '' } = options;

  if (!address) return fallback;

  // Handle JSON strings
  if (typeof address === 'string') {
    if (address.startsWith('{')) {
      try {
        const parsed = JSON.parse(address);
        if (typeof parsed === 'object' && parsed !== null) {
          return formatAddress(parsed, options);
        }
      } catch (e) {
        // Not valid JSON, treat as plain string
      }
    }
    // Plain string — normalize separators and strip country
    let str = address;
    if (separator === '\n' && !str.includes('\n') && str.includes(', ')) {
      str = str.replace(/, /g, '\n');
    } else if (separator === ', ' && str.includes('\n')) {
      str = str.split('\n').filter(p => p && p.trim()).join(', ');
    }
    return stripCountryFromString(str, separator) || fallback;
  }

  if (typeof address !== 'object') return String(address);

  const parts = extractParts(address, { includeAttention });

  // Remove customer name from first line if it matches (Xero import quirk)
  if (stripName && parts.length > 0 && parts[0].trim().toLowerCase() === stripName.trim().toLowerCase()) {
    parts.shift();
  }

  return parts.join(separator) || fallback;
}

/**
 * Format address from flat database columns using a prefix (e.g. 'delivery_', 'invoice_').
 * Used for contact records that store addresses in flat columns.
 *
 * @param {Object} obj - Object containing flat address columns
 * @param {string} prefix - Column prefix (e.g. 'delivery_', 'invoice_')
 * @param {Object} [options] - Same options as formatAddress
 * @returns {string}
 */
export function formatAddressFromFlat(obj, prefix, options = {}) {
  if (!obj) return options.fallback || '';
  const address = {
    // attention: obj[prefix + 'attention'],
    line1: obj[prefix + 'line1'],
    line2: obj[prefix + 'line2'],
    line3: obj[prefix + 'line3'],
    line4: obj[prefix + 'line4'],
    city: obj[prefix + 'city'],
    region: obj[prefix + 'region'],
    postcode: obj[prefix + 'postcode'],
    // Country intentionally excluded from display
  };
  return formatAddress(address, { includeAttention: true, ...options });
}

/**
 * Extract just the first delivery address line from a contact's stored `address`
 * value (a JSON string or object as Xero/our DB stores it). Returns '' when
 * absent or unparseable. Used to show "Company Name (Address line 1)" alongside
 * a contact name in dropdowns, pricing tables and contact groups.
 *
 * @param {string|object|null} address
 * @returns {string}
 */
export function addressLine1(address) {
  if (!address) return '';
  try {
    const addr = typeof address === 'string' ? JSON.parse(address) : address;
    return (addr && addr.addressLine1) ? addr.addressLine1 : '';
  } catch (e) {
    return '';
  }
}
